import React from 'react';
import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

const FORMAT = 'YYYY-MM-DD';

const toDayjs = (value?: string) => {
  if (!value) return null;
  const d = dayjs(value, FORMAT, true);
  return d.isValid() ? d : null;
};

export interface DateFieldProps {
  /** YYYY-MM-DD, or '' for no date. */
  value: string;
  onChange: (value: string) => void;
  label?: string;
  min?: string;
  max?: string;
  helperText?: React.ReactNode;
  error?: boolean;
  disabled?: boolean;
  size?: 'small' | 'medium';
  sx?: SxProps<Theme>;
  'aria-label'?: string;
}

/** DatePicker with string YYYY-MM-DD in and out. Brings its own LocalizationProvider. */
export const DateField: React.FC<DateFieldProps> = ({
  value,
  onChange,
  label,
  min,
  max,
  helperText,
  error,
  disabled,
  size = 'small',
  sx,
  'aria-label': ariaLabel,
}) => (
  <LocalizationProvider dateAdapter={AdapterDayjs}>
    <DatePicker
      label={label}
      value={toDayjs(value)}
      onChange={(next) => {
        if (next === null) onChange('');
        else if (next.isValid()) onChange(next.format(FORMAT));
      }}
      minDate={toDayjs(min) ?? undefined}
      maxDate={toDayjs(max) ?? undefined}
      disabled={disabled}
      format={FORMAT}
      slotProps={{
        textField: {
          size,
          error,
          helperText,
          sx,
          inputProps: ariaLabel ? { 'aria-label': ariaLabel } : undefined,
        },
      }}
    />
  </LocalizationProvider>
);

export interface DateRangeFieldsProps {
  start: string;
  end: string;
  onStartChange: (value: string) => void;
  onEndChange: (value: string) => void;
  startLabel?: string;
  endLabel?: string;
  min?: string;
  max?: string;
  disabled?: boolean;
  size?: 'small' | 'medium';
  sx?: SxProps<Theme>;
}

/** Start and end pair; flags the end field when it falls before the start. */
export const DateRangeFields: React.FC<DateRangeFieldsProps> = ({
  start,
  end,
  onStartChange,
  onEndChange,
  startLabel = 'Start date',
  endLabel = 'End date',
  min,
  max,
  disabled,
  size = 'small',
  sx,
}) => {
  const invalid = Boolean(start && end && end < start);
  return (
    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-start', ...(sx as object) }}>
      <DateField
        label={startLabel}
        value={start}
        onChange={onStartChange}
        min={min}
        max={max}
        disabled={disabled}
        size={size}
      />
      <DateField
        label={endLabel}
        value={end}
        onChange={onEndChange}
        min={min}
        max={max}
        disabled={disabled}
        size={size}
        error={invalid}
        helperText={invalid ? 'End date is before the start date' : undefined}
      />
    </Box>
  );
};
